import {
  loadActiveIterationByMonth,
  loadIterations,
  loadReviews,
  overwriteReviews,
  saveActiveIteration,
  saveIterations,
  upsertIteration,
} from './persistence.js';
import { reconcile } from './reconcile.js';

const ISO_NOW = () => new Date().toISOString();

/** Derive a plausible (category, remediation, note) for a reviewed BAC,
 *  based on the variance driver surfaced by reconcile(). Used only when
 *  seeding "Round 1 · already reviewed" snapshot data for the mock. */
const suggestedTreatment = (row) => {
  const d = row.varianceDriver;
  if (!d) return null;
  const map = {
    'CPQ stale vs GM':      { category: 'Pricing mismatch',       remediation: 'Updated CPQ subscription price', note: 'CPQ hadn\'t caught up with GM. Updated subscription to match.' },
    'GM billing lag':       { category: 'Package tier change',    remediation: 'Submitted GM adjustment ticket', note: 'GM hadn\'t propagated the change. Ticket filed for next cycle.' },
    'Three-way drift':      { category: 'Pricing mismatch',       remediation: 'Updated CPQ subscription price', note: 'Brought CPQ back in line with GM; DPE refreshed.' },
    'Missing in GM':        { category: 'Missing in GM',          remediation: 'No action needed (within tolerance)', note: 'Expected to appear in GM next cycle.' },
    'Missing in CPQ':       { category: 'Missing in CPQ',         remediation: 'Added missing product to CPQ',  note: 'Backfilled CPQ subscription.' },
    'Missing in DPE':       { category: 'DPE stale data',         remediation: 'Refreshed DPE program cache',   note: 'DPE cache refresh resolved.' },
    'Only in GM':           { category: 'Missing in CPQ',         remediation: 'Added missing product to CPQ',  note: 'Enrolled BAC in CPQ to match GM billing.' },
    'Only in CPQ':          { category: 'Missing in GM',          remediation: 'No action needed (within tolerance)', note: 'Expected to appear in GM next cycle.' },
    'Only in DPE':          { category: 'DPE stale data',         remediation: 'Refreshed DPE program cache',   note: 'Stale DPE cache — cleared.' },
    'No data anywhere':     { category: 'Duplicate entry',        remediation: 'Cancelled duplicate in CPQ',    note: 'Orphan BAC — confirmed with billing team.' },
    'Pending activation':   { category: 'Cancellation timing',    remediation: 'Confirmed with billing team',   note: 'Pending activation — no action required this cycle.' },
    'Wind-down in progress':{ category: 'Cancellation timing',    remediation: 'Confirmed with billing team',   note: 'Wind-down on schedule.' },
    'DPE drift':            { category: 'DPE stale data',         remediation: 'Refreshed DPE program cache',   note: 'DPE-only drift; refresh requested.' },
  };
  return map[d.label] || { category: 'Pricing mismatch', remediation: 'Updated CPQ subscription price', note: 'Investigated and corrected.' };
};

/** Given the current mock data, produce a review map for "Round 1" where
 *  every variance has been signed off with a plausible treatment. This makes
 *  the first iteration look like a completed cycle when flipping back to it. */
export const seedCompletedRoundReviews = ({ gmRows, cpqRows, dpeDealers, serviceMonth }) => {
  const rows = reconcile({ gmRows, cpqRows, dpeDealers, serviceMonth });
  const reviews = {};
  const APR_1_TS = '2026-04-05T14:12:00Z';
  for (const r of rows) {
    // Skip matches — they don't need a review
    if (Math.abs(r.deltas.gmVsCpq) <= 1) continue;
    const treatment = suggestedTreatment(r);
    if (!treatment) continue;
    reviews[r.bac] = {
      reviewed: true,
      category: treatment.category,
      remediation: treatment.remediation,
      notes: [{
        id: `seed-${r.bac}-1`,
        ts: APR_1_TS,
        text: treatment.note,
        author: 'Morgan R.',
        status: 'final',
      }],
    };
  }
  return reviews;
};

/**
 * One-time bootstrap: on first page load, if the user has no iterations yet,
 * seed a couple for the current month so the UX is immediately populated.
 * Idempotent — checks before writing.
 */
export const ensureSeededIterations = ({ seedData, currentMonthId = 'APR' }) => {
  const existing = loadIterations();
  if (existing.length > 0) return;

  const round1 = {
    id: `${currentMonthId}-1`,
    monthId: currentMonthId,
    number: 1,
    createdAt: '2026-04-05T10:00:00Z',
    label: 'Initial audit',
    gmFileName: null,
    cpqFileName: null,
  };
  const round2 = {
    id: `${currentMonthId}-2`,
    monthId: currentMonthId,
    number: 2,
    createdAt: ISO_NOW(),
    label: 'After GM feedback',
    gmFileName: null,
    cpqFileName: null,
  };

  saveIterations([round1, round2]);
  saveActiveIteration(currentMonthId, round2.id);

  // Pre-populate Round 1 with all variances already reviewed (so flipping
  // back to it shows the cleared banner + full treatment history).
  if (seedData) {
    const reviews = seedCompletedRoundReviews({ ...seedData, serviceMonth: currentMonthId });
    overwriteReviews(round1.id, reviews);
  }
};

/**
 * When a new round is opened, copy the prior round's review state forward so
 * the investigation trail is preserved. Two cases:
 *
 *   1. Variance carries over (still |GM − CPQ| > threshold): the user
 *      shouldn't have to re-investigate what they already figured out —
 *      prepend a "Carried over from Round N" marker to the notes.
 *   2. Variance is resolved in the new round (|GM − CPQ| ≤ threshold): keep
 *      the investigation history for future reference, prepend a
 *      "Resolved in Round N" marker so the audit trail is clear.
 *
 * Each carried-over review is tagged with `inheritedFrom`,
 * `inheritedFromRound`, and `inheritedFromDate` so the UI can badge it.
 *
 * Returns the breakdown: { total, resolved, carriedVariance }.
 */
export const inheritReviewsFromPriorRound = ({
  newIterationId,
  monthId,
  gmRows,
  cpqRows,
  dpeDealers,
  threshold = 1,
}) => {
  const all = loadIterations();
  const forMonth = all
    .filter((it) => it.monthId === monthId)
    .sort((a, b) => (a.number || 0) - (b.number || 0));
  const newIdx = forMonth.findIndex((it) => it.id === newIterationId);
  if (newIdx <= 0) return { total: 0, resolved: 0, carriedVariance: 0 };

  const prior = forMonth[newIdx - 1];
  const priorReviews = loadReviews(prior.id);
  if (!priorReviews || Object.keys(priorReviews).length === 0) {
    return { total: 0, resolved: 0, carriedVariance: 0 };
  }

  const rows = reconcile({ gmRows, cpqRows, dpeDealers, serviceMonth: monthId });
  const rowByBac = new Map(rows.map((r) => [r.bac, r]));
  const newIteration = all.find((it) => it.id === newIterationId);

  const priorDateLabel = new Date(prior.createdAt).toLocaleDateString();
  const makeMarker = (text, idSuffix) => ({
    id: `sys-${newIterationId}-${idSuffix}`,
    ts: ISO_NOW(),
    text,
    author: 'System',
    status: 'system',
  });
  const carryOverMarker = (bac) =>
    makeMarker(
      `Carried over from ${prior.label} (Round ${prior.number}, ${priorDateLabel}) — same variance appeared again. Prior investigation notes preserved below.`,
      `carry-${bac}`,
    );
  const resolvedMarker = (bac) =>
    makeMarker(
      `Variance from ${prior.label} (Round ${prior.number}, ${priorDateLabel}) has resolved in Round ${newIteration?.number || newIdx + 1} — GM and CPQ now match. Keeping the investigation history for reference.`,
      `resolved-${bac}`,
    );

  const inherited = {};
  let resolved = 0;
  let carriedVariance = 0;

  for (const [bac, prev] of Object.entries(priorReviews)) {
    // If we can't find the BAC in the current reconciliation, skip — it was
    // removed from the files.
    const row = rowByBac.get(bac);
    if (!row) continue;

    const gmCpqAbs = Math.abs(row.deltas?.gmVsCpq || 0);
    const isResolvedNow = gmCpqAbs <= threshold;
    const marker = isResolvedNow ? resolvedMarker(bac) : carryOverMarker(bac);

    inherited[bac] = {
      reviewed: Boolean(prev.reviewed),
      category: prev.category || null,
      remediation: prev.remediation || null,
      notes: [marker, ...(Array.isArray(prev.notes) ? prev.notes : [])],
      inheritedFrom: prior.id,
      inheritedFromRound: prior.number,
      inheritedFromLabel: prior.label,
      inheritedFromDate: prior.createdAt,
      inheritedResolved: isResolvedNow,
    };

    if (isResolvedNow) resolved += 1;
    else carriedVariance += 1;
  }

  if (Object.keys(inherited).length > 0) {
    overwriteReviews(newIterationId, inherited);
  }
  return { total: Object.keys(inherited).length, resolved, carriedVariance };
};

/** Number-bumping constructor for a new iteration within a month. */
export const createNextIteration = (monthId, label) => {
  const all = loadIterations();
  const forMonth = all.filter((it) => it.monthId === monthId);
  const nextNumber = forMonth.length
    ? Math.max(...forMonth.map((it) => it.number || 0)) + 1
    : 1;
  const iteration = {
    id: `${monthId}-${nextNumber}`,
    monthId,
    number: nextNumber,
    createdAt: ISO_NOW(),
    label: label || `Round ${nextNumber}`,
    gmFileName: null,
    cpqFileName: null,
  };
  return upsertIteration(iteration);
};

/** Figure out which iteration should be active for a given service month:
 *  first honor the user's last choice, otherwise pick the newest. If the
 *  month has no iterations at all, create one. */
export const resolveActiveIteration = (monthId) => {
  const all = loadIterations();
  let forMonth = all.filter((it) => it.monthId === monthId);
  if (forMonth.length === 0) {
    const created = createNextIteration(monthId);
    forMonth = [created];
  }
  const stored = loadActiveIterationByMonth()[monthId];
  const picked = forMonth.find((it) => it.id === stored) || forMonth[forMonth.length - 1];
  if (picked.id !== stored) saveActiveIteration(monthId, picked.id);
  return { iterations: loadIterations(), active: picked };
};

/** Convenience: get all iterations for a single month, newest first. */
export const iterationsForMonth = (iterations, monthId) =>
  iterations
    .filter((it) => it.monthId === monthId)
    .sort((a, b) => (a.number || 0) - (b.number || 0));

/** Lightweight summary for a timeline chip: reads stored reviews +
 *  reconciles the given seed to report {reviewedCount, remainingAbs}. */
export const summarizeIteration = ({ iteration, gmRows, cpqRows, dpeDealers }) => {
  const reviews = loadReviews(iteration.id);
  const rows = reconcile({ gmRows, cpqRows, dpeDealers, serviceMonth: iteration.monthId });
  let remainingAbs = 0;
  let reviewedCount = 0;
  let openCount = 0;
  for (const r of rows) {
    const gmCpqAbs = Math.abs(r.deltas.gmVsCpq);
    const isReviewed = Boolean(reviews[r.bac]?.reviewed);
    if (gmCpqAbs <= 1) continue;
    if (isReviewed) reviewedCount += 1;
    else {
      openCount += 1;
      remainingAbs += gmCpqAbs;
    }
  }
  return { reviewedCount, openCount, remainingAbs: Math.round(remainingAbs * 100) / 100 };
};
